import { api } from '../../api';

// Tipos para las respuestas del dashboard
export interface NomineesSummaryResponse {
  totalNominees: number;
  activeNominees: number;
  totalResells: number;
  totalAmount: number;
  averageResellsPerNominee: number;
  byStatus: {
    status: string;
    count: number;
    amount: number;
  }[];
  topNominees: {
    nomineeId: number;
    resellsCount: number;
    totalAmount: number;
    averageAmount: number;
  }[];
}

export interface NomineesAnalyticsResponse {
  nomineesByPeriod: {
    period: string;
    nominees: number;
    resells: number;
    amount: number;
  }[];
  nomineesByFund: {
    fundId: string;
    fundName: string;
    nominees: number;
    resells: number;
    amount: number;
  }[];
  nomineesByBusiness: {
    businessId: string;
    businessName: string;
    nominees: number;
    resells: number;
    amount: number;
  }[];
}

export interface ResellsConsolidatedResponse {
  individualResells: {
    total: number;
    active: number;
    amount: number;
    byStatus: { status: string; count: number; amount: number }[];
  };
  nomineesResells: {
    total: number;
    active: number;
    amount: number;
    averageResellsPerNominee: number;
  };
  comparison: {
    individualVsNominees: {
      individualPercentage: number;
      nomineesPercentage: number;
    };
  };
}

export interface PaymentsConsolidatedResponse {
  totalPayments: number;
  totalAmount: number;
  pendingPayments: number;
  pendingAmount: number;
  averagePaymentAmount: number;
  averagePaymentTime: number;
  byFund: {
    fundId: string;
    fundName: string;
    payments: number;
    amount: number;
    pendingAmount: number;
  }[];
  paymentTrends: {
    date: string;
    payments: number;
    amount: number;
  }[];
}

export interface OverdueAndDueDatesResponse {
  // Resumen general
  totalOverdue: number;
  totalOverdueAmount: number;
  totalDueSoon: number;
  totalDueSoonAmount: number;
  averageOverdueDays: number;

  // Desglose por fondo
  byFund: FundOverdueSummary[];

  // Desglose por días de mora
  overdueByDays: OverdueByDaysRange[];

  // Vencimientos próximos por días
  dueSoonByDays: DueSoonByDaysRange[];

  // Top reventas en mora por monto
  topOverdueResells: TopOverdueResell[];
}

export interface FundOverdueSummary {
  fundId: string;
  fundName: string;
  overdueCount: number;
  overdueAmount: number;
  dueSoonCount: number;
  dueSoonAmount: number;
  averageOverdueDays: number;
}

export interface OverdueByDaysRange {
  range: string;
  count: number;
  amount: number;
}

export interface DueSoonByDaysRange {
  range: string;
  count: number;
  amount: number;
}

export interface TopOverdueResell {
  resellId: number;
  businessName: string;
  stakeholderName: string;
  amount: number;
  overdueDays: number;
  fundName: string;
}

export interface FundsDashboardData {
  nomineesSummary: NomineesSummaryResponse;
  nomineesAnalytics: NomineesAnalyticsResponse;
  resellsConsolidated: ResellsConsolidatedResponse;
  paymentsConsolidated: PaymentsConsolidatedResponse;
  overdueAndDueDates: OverdueAndDueDatesResponse;
}

// Función para obtener el resumen de nóminas
export const getNomineesSummary =
  async (): Promise<NomineesSummaryResponse> => {
    try {
      const response = await api.get(
        '/internal/funds-dashboard/nominees/summary',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching nominees summary:', error);
      throw error;
    }
  };

// Función para obtener análisis de nóminas
export const getNomineesAnalytics =
  async (): Promise<NomineesAnalyticsResponse> => {
    try {
      const response = await api.get(
        '/internal/funds-dashboard/nominees/analytics',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching nominees analytics:', error);
      throw error;
    }
  };

// Función para obtener resumen consolidado de reventas
export const getResellsConsolidated =
  async (): Promise<ResellsConsolidatedResponse> => {
    try {
      const response = await api.get(
        '/internal/funds-dashboard/resells/consolidated',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching resells consolidated:', error);
      throw error;
    }
  };

// Función para obtener pagos consolidados
export const getPaymentsConsolidated =
  async (): Promise<PaymentsConsolidatedResponse> => {
    try {
      const response = await api.get(
        '/internal/funds-dashboard/payments/consolidated',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payments consolidated:', error);
      throw error;
    }
  };

export const getOverdueAndDueDates =
  async (): Promise<OverdueAndDueDatesResponse> => {
    try {
      const response = await api.get(
        '/internal/funds-dashboard/overdue-and-due-dates',
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching overdue and due dates:', error);
      throw error;
    }
  };

// Función principal para obtener todos los datos del dashboard
export const getFundsDashboardData = async (): Promise<FundsDashboardData> => {
  try {
    const [
      nomineesSummary,
      nomineesAnalytics,
      resellsConsolidated,
      paymentsConsolidated,
      overdueAndDueDates,
    ] = await Promise.all([
      getNomineesSummary(),
      getNomineesAnalytics(),
      getResellsConsolidated(),
      getPaymentsConsolidated(),
      getOverdueAndDueDates(),
    ]);

    return {
      nomineesSummary,
      nomineesAnalytics,
      resellsConsolidated,
      paymentsConsolidated,
      overdueAndDueDates,
    };
  } catch (error) {
    console.error('Error fetching funds dashboard data:', error);
    throw error;
  }
};
